import { useContext } from 'react';
import { Box, IconButton, Snackbar, Stack, Typography } from '@mui/joy';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import { motion } from 'framer-motion';

import { LayoutContext } from '../../context';

const MotionBox = motion(Box);

export default function NotificationBell() {
  const { openNotif, setOpenNotif } = useContext(LayoutContext);

  const formatTime = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diff = now - time;
    if (isNaN(diff)) return 'только что';
    if (diff < 60000) return 'только что';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} мин назад`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)} ч назад`;
    return time.toLocaleDateString();
  };

  const close = () => setOpenNotif({ open: false, text: null, chat: null });

  const sender = (openNotif?.sender || 'Уведомление').toString();
  const initial = sender.trim().charAt(0).toUpperCase() || '•';
  // Не показываем «имя чата», если это просто числовой id (например «2489»).
  const chatName = openNotif?.chat && !/^\d+$/.test(String(openNotif.chat))
    ? String(openNotif.chat) : null;

  const goToChat = () => {
    if (openNotif?.chatId) window.location.href = `/chats?chatId=${openNotif.chatId}`;
  };

  return (
    <Snackbar
      autoHideDuration={5000}
      open={!!openNotif?.open}
      variant="plain"
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      onClose={(event, reason) => { if (reason !== 'clickaway') close(); }}
      sx={{ p: 0, bgcolor: 'transparent', boxShadow: 'none', border: 'none' }}
    >
      <MotionBox
        initial={{ opacity: 0, y: -16, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: 'spring', stiffness: 420, damping: 30 }}
        onClick={goToChat}
        sx={{
          position: 'relative', width: 360, maxWidth: '90vw',
          borderRadius: '16px', overflow: 'hidden',
          cursor: openNotif?.chatId ? 'pointer' : 'default',
          bgcolor: 'background.surface',
          border: '1px solid rgba(99,102,241,0.18)',
          boxShadow: '0 20px 48px -20px rgba(79,70,229,0.55), 0 8px 24px -16px rgba(0,0,0,0.35)',
        }}
      >
        {/* Верхняя градиентная полоса */}
        <Box sx={{ height: 4, background: 'linear-gradient(90deg,#6366f1,#a855f7,#ec4899)' }} />

        <Box sx={{ display: 'flex', gap: 1.25, p: 1.5 }}>
          {/* Аватар отправителя */}
          <Box sx={{
            flexShrink: 0, width: 40, height: 40, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#fff', fontWeight: 800, fontSize: 16,
            background: 'linear-gradient(135deg,#6366f1,#a855f7)',
            boxShadow: '0 6px 16px -6px rgba(99,102,241,0.8)',
          }}>
            {initial}
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" sx={{ alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
              <Typography level="title-sm" sx={{ fontWeight: 700 }} noWrap>
                {sender}
              </Typography>
              <IconButton
                onClick={(e) => { e.stopPropagation(); close(); }}
                size="sm" variant="plain" color="neutral"
                sx={{ '--IconButton-size': '26px', borderRadius: '50%', mr: -0.5, mt: -0.5 }}
              >
                <CloseRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Stack>

            {chatName && (
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.25 }} noWrap>
                {chatName}
              </Typography>
            )}

            <Typography
              level="body-sm"
              sx={{
                color: 'text.secondary',
                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {openNotif?.text}
            </Typography>

            <Typography
              level="body-xs"
              startDecorator={<ChatRoundedIcon sx={{ fontSize: 14 }} />}
              sx={{ mt: 0.75, color: 'primary.500', fontWeight: 600 }}
            >
              Новое сообщение · {formatTime(openNotif?.timestamp)}
            </Typography>
          </Box>
        </Box>
      </MotionBox>
    </Snackbar>
  );
}